import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { LanguageSwitch } from '../../shared/layout/LanguageSwitch';
import { ThemeToggle } from '../../shared/layout/ThemeToggle';
import { LegalLinks } from '../legal';
import { Icon, type IconName } from '../../shared/ui';
import styles from './landing.module.css';

type Lang = 'en' | 'ar';
interface Shot {
  name: string;
  w: number;
  h: number;
}
/** Screenshots exist once per language, under /landing/<lang>/. */
const desktop = (name: string): Shot => ({ name, w: 1440, h: 900 });
const phone = (name: string): Shot => ({ name, w: 540, h: 1169 });
const src = (lang: Lang, shot: Shot) => `/landing/${lang}/${shot.name}.webp`;

/** The five ways to add items, in tab order. */
const WAYS = [
  { id: 'search', icon: 'search', shot: phone('add-search') },
  { id: 'categories', icon: 'grid', shot: phone('add-categories') },
  { id: 'meals', icon: 'meals', shot: phone('add-meal') },
  { id: 'ai', icon: 'sparkles', shot: phone('add-ai') },
  { id: 'manual', icon: 'edit', shot: phone('add-manual') },
] as const satisfies ReadonlyArray<{ id: string; icon: IconName; shot: Shot }>;
type WayId = (typeof WAYS)[number]['id'];

/** Order matters: it is the order on the page. */
const FEATURES = [
  { id: 'listview', shot: desktop('list') },
  { id: 'favorites', shot: desktop('favorites') },
] as const;

const EXTRAS = [
  { id: 'colours', icon: 'lists' },
  { id: 'sponsored', icon: 'cart' },
  { id: 'pdf', icon: 'pdf' },
] as const satisfies ReadonlyArray<{ id: string; icon: IconName }>;

function Figure({
  lang,
  shot,
  alt,
  isPhone,
  className,
}: {
  lang: Lang;
  shot: Shot;
  alt: string;
  isPhone?: boolean;
  className?: string;
}) {
  return (
    <figure
      className={[styles.figure, isPhone ? styles.phoneFrame : styles.browserFrame, className]
        .filter(Boolean)
        .join(' ')}
    >
      {!isPhone && (
        <div className={styles.browserBar} aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      )}
      <img
        src={src(lang, shot)}
        width={shot.w}
        height={shot.h}
        alt={alt}
        loading="lazy"
        decoding="async"
      />
    </figure>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className={styles.bullets}>
      {items.map((b) => (
        <li key={b}>
          <Icon name="check" size={18} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

/** "Five ways to add items": a tab per way, each with a phone screenshot (WAI-ARIA tabs). */
function AddWays({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const [active, setActive] = useState<WayId>('search');
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKey = (e: KeyboardEvent, i: number) => {
    const rtl = document.documentElement.dir === 'rtl';
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[e.key];
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? WAYS.length - 1
          : step !== undefined
            ? (i + step + WAYS.length) % WAYS.length
            : undefined;
    if (next === undefined) return;
    e.preventDefault();
    setActive(WAYS[next]!.id);
    tabs.current[next]?.focus();
  };

  return (
    <>
      <div className={styles.tabs} role="tablist" aria-label={t('landing.add.tabsLabel')}>
        {WAYS.map((w, i) => (
          <button
            key={w.id}
            ref={(el) => {
              tabs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`way-tab-${w.id}`}
            aria-controls={`way-panel-${w.id}`}
            aria-selected={active === w.id}
            tabIndex={active === w.id ? 0 : -1}
            className={styles.tab}
            onClick={() => setActive(w.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <Icon name={w.icon} size={18} />
            {t(`landing.add.w.${w.id}.tab`)}
          </button>
        ))}
      </div>
      {WAYS.map((w, i) => (
        <div
          key={w.id}
          role="tabpanel"
          id={`way-panel-${w.id}`}
          aria-labelledby={`way-tab-${w.id}`}
          hidden={active !== w.id}
          className={styles.wayPanel}
        >
          <div className={styles.wayText}>
            <span className={styles.wayStep} aria-hidden="true">
              {i + 1}/{WAYS.length}
            </span>
            <h3>{t(`landing.add.w.${w.id}.title`)}</h3>
            <p>{t(`landing.add.w.${w.id}.body`)}</p>
            <Bullets
              items={t(`landing.add.w.${w.id}.b`, { returnObjects: true }) as unknown as string[]}
            />
          </div>
          <Figure lang={lang} shot={w.shot} alt={t(`landing.add.w.${w.id}.alt`)} isPhone />
        </div>
      ))}
    </>
  );
}

/** Public home page for visitors who are not signed in: what Lista is, what it does, and how to join. */
export function LandingPage() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'ar' ? 'ar' : 'en';
  const other: Lang = lang === 'ar' ? 'en' : 'ar';

  useEffect(() => {
    const before = document.title;
    document.title = t('landing.meta.title');
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.append(meta);
    }
    const prev = meta.content;
    meta.content = t('landing.meta.description');
    return () => {
      document.title = before;
      if (created) meta?.remove();
      else if (meta) meta.content = prev;
    };
  }, [t]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logo} aria-label={t('landing.nav.home')}>
            Lista
          </Link>
          <nav className={styles.headerNav} aria-label={t('landing.nav.menu')}>
            <a href="#add-items" className={styles.navLink}>
              {t('landing.nav.addItems')}
            </a>
            <a href="#sharing" className={styles.navLink}>
              {t('landing.nav.sharing')}
            </a>
            <a href="#features" className={styles.navLink}>
              {t('landing.nav.features')}
            </a>
            <ThemeToggle className={styles.lang} />
            <LanguageSwitch className={styles.lang} />
            <Link to="/login" className={styles.loginLink}>
              {t('landing.nav.login')}
            </Link>
            <Link to="/register" className={styles.registerLink}>
              {t('landing.nav.register')}
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroText}>
            <p className={styles.eyebrow}>{t('landing.hero.eyebrow')}</p>
            <h1 id="hero-title" className={styles.heroTitle}>
              {t('landing.hero.title')}
            </h1>
            <p className={styles.heroBody}>{t('landing.hero.body')}</p>
            <div className={styles.ctaRow}>
              <Link to="/register" className={styles.cta}>
                {t('landing.hero.cta')}
              </Link>
              <Link to="/login" className={styles.ctaSecondary}>
                {t('landing.hero.login')}
              </Link>
            </div>
            <p className={styles.note}>{t('landing.hero.note')}</p>
          </div>
          <div className={styles.heroVisual}>
            <img
              className={styles.heroDesktop}
              src={src(lang, desktop('lists'))}
              width={1440}
              height={900}
              alt={t('landing.hero.imageAlt')}
              fetchPriority="high"
            />
            <img
              className={styles.heroPhone}
              src={src(lang, phone('phone-list'))}
              width={540}
              height={1169}
              alt={t('landing.hero.phoneAlt')}
            />
          </div>
        </section>

        <section id="add-items" className={styles.section} aria-labelledby="add-title">
          <div className={styles.sectionHead}>
            <h2 id="add-title">{t('landing.add.title')}</h2>
            <p>{t('landing.add.intro')}</p>
          </div>
          <AddWays lang={lang} />
        </section>

        <section id="sharing" className={styles.band} aria-labelledby="share-title">
          <div className={styles.bandInner}>
            <div className={styles.sectionHead}>
              <p className={styles.eyebrow}>
                <span className={styles.liveDot} aria-hidden="true" />
                {t('landing.share.eyebrow')}
              </p>
              <h2 id="share-title">{t('landing.share.title')}</h2>
              <p>{t('landing.share.intro')}</p>
            </div>

            <article className={styles.feature}>
              <div className={styles.featureText}>
                <h3>{t('landing.share.together.title')}</h3>
                <p>{t('landing.share.together.body')}</p>
                <Bullets
                  items={
                    t('landing.share.together.b', { returnObjects: true }) as unknown as string[]
                  }
                />
              </div>
              <div className={styles.duo}>
                <div className={styles.duoPhone}>
                  <Figure
                    lang={lang}
                    shot={phone('phone-list')}
                    alt={t('landing.share.ownerAlt')}
                    isPhone
                  />
                  <span className={styles.tag}>{t('landing.share.ownerTag')}</span>
                </div>
                <div className={styles.duoPhone}>
                  <Figure
                    lang={lang}
                    shot={phone('partner')}
                    alt={t('landing.share.partnerAlt')}
                    isPhone
                  />
                  <span className={styles.tag}>{t('landing.share.partnerTag')}</span>
                </div>
              </div>
            </article>

            <article className={styles.feature} data-flip>
              <div className={styles.featureText}>
                <h3>{t('landing.share.invite.title')}</h3>
                <p>{t('landing.share.invite.body')}</p>
                <Bullets
                  items={
                    t('landing.share.invite.b', { returnObjects: true }) as unknown as string[]
                  }
                />
              </div>
              <div className={styles.composite}>
                <img
                  className={styles.dialogShot}
                  src={src(lang, { name: 'share', w: 900, h: 1005 })}
                  width={900}
                  height={1005}
                  alt={t('landing.share.invite.alt')}
                  loading="lazy"
                  decoding="async"
                />
                <Figure
                  lang={lang}
                  shot={phone('public-link')}
                  alt={t('landing.share.invite.linkAlt')}
                  isPhone
                  className={styles.overlapPhone}
                />
              </div>
            </article>
          </div>
        </section>

        <section id="features" className={styles.section} aria-labelledby="features-title">
          <div className={styles.sectionHead}>
            <h2 id="features-title">{t('landing.featuresTitle')}</h2>
            <p>{t('landing.featuresIntro')}</p>
          </div>
          {FEATURES.map((f, i) => (
            <article key={f.id} className={styles.feature} data-flip={i % 2 === 1 || undefined}>
              <div className={styles.featureText}>
                <h3>{t(`landing.f.${f.id}.title`)}</h3>
                <p>{t(`landing.f.${f.id}.body`)}</p>
                <Bullets
                  items={t(`landing.f.${f.id}.b`, { returnObjects: true }) as unknown as string[]}
                />
              </div>
              <Figure lang={lang} shot={f.shot} alt={t(`landing.f.${f.id}.alt`)} />
            </article>
          ))}

          <h2 className={styles.subHead}>{t('landing.extrasTitle')}</h2>
          <div className={styles.extras}>
            {EXTRAS.map((x) => (
              <div key={x.id} className={styles.extra}>
                <span className={styles.extraIcon}>
                  <Icon name={x.icon} size={22} />
                </span>
                <h3>{t(`landing.extras.${x.id}.title`)}</h3>
                <p>{t(`landing.extras.${x.id}.body`)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section} aria-labelledby="more-title">
          <div className={styles.sectionHead}>
            <h2 id="more-title">{t('landing.moreTitle')}</h2>
          </div>
          <div className={styles.moreGrid}>
            <div className={styles.moreCard}>
              {/* The other language's screenshot shows the app really is bilingual. */}
              <Figure lang={other} shot={desktop('lists')} alt={t('landing.more.language.alt')} />
              <h3>{t('landing.more.language.title')}</h3>
              <p>{t('landing.more.language.body')}</p>
            </div>
            <div className={styles.moreCard}>
              <Figure lang={lang} shot={desktop('dark')} alt={t('landing.more.dark.alt')} />
              <h3>{t('landing.more.dark.title')}</h3>
              <p>{t('landing.more.dark.body')}</p>
            </div>
            <div className={styles.moreCard}>
              <Figure
                lang={lang}
                shot={phone('phone-lists')}
                alt={t('landing.more.phone.alt')}
                isPhone
              />
              <h3>{t('landing.more.phone.title')}</h3>
              <p>{t('landing.more.phone.body')}</p>
            </div>
          </div>
        </section>

        <section className={styles.final} aria-labelledby="final-title">
          <h2 id="final-title">{t('landing.final.title')}</h2>
          <p>{t('landing.final.body')}</p>
          <div className={styles.ctaRow}>
            <Link to="/register" className={styles.cta}>
              {t('landing.final.cta')}
            </Link>
            <Link to="/login" className={styles.ctaSecondary}>
              {t('landing.final.login')}
            </Link>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <Link to="/" className={styles.logo}>
          Lista
        </Link>
        <p>{t('landing.footer.rights', { year: new Date().getFullYear() })}</p>
        <LegalLinks />
      </footer>
    </div>
  );
}
